
import { useState, useEffect } from 'react';
import { mockLGAs } from '@/data/nigerianStates';
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Share2, QrCode } from 'lucide-react';
import QRModal from './QRModal';
import LGAHeader from './LGAHeader';
import LGATabs from './LGATabs';
import { toast } from 'sonner';
import { generateReferralUrl } from '@/utils/referralUtils';

interface LGASectionProps {
  state: string;
  isAdmin: boolean;
  userId?: string;
  referralUrl: string;
}

const LGASection = ({ 
  state, 
  isAdmin,
  userId,
  referralUrl
}: LGASectionProps) => {
  const [loading, setLoading] = useState(true);
  const [localGovts, setLocalGovts] = useState<string[]>([]);
  const [selectedLGA, setSelectedLGA] = useState<string | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);
  
  // Load LGAs for the selected state
  useEffect(() => {
    // Simulate network delay (would fetch from API in production)
    const timer = setTimeout(() => {
      // Get LGAs from the mock data for now
      const lgas = state in mockLGAs ? mockLGAs[state as keyof typeof mockLGAs] : [];
      setLocalGovts(lgas);
      setLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [state]);
  
  // Copy referral link to clipboard
  const copyReferralLink = () => {
    if (referralUrl) {
      navigator.clipboard.writeText(referralUrl)
        .then(() => {
          toast.success('Referral link copied to clipboard');
          console.log("Copied URL:", referralUrl); // Add logging
        })
        .catch(() => toast.error('Failed to copy link'));
    } else {
      toast.error('Referral link not available');
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Referral sharing buttons */}
      {userId && (
        <div className="flex justify-end space-x-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={copyReferralLink}
            className="text-xs"
          >
            <Share2 size={14} className="mr-1"/> Share Invite
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowQRModal(true)}
            className="text-xs"
          >
            <QrCode size={14} className="mr-1"/> QR Code
          </Button>
        </div>
      )}
    
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {localGovts.length > 0 ? (
              localGovts.map((lga) => (
                <Button 
                  key={lga}
                  variant={selectedLGA === lga ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedLGA(lga)}
                >
                  {lga}
                </Button>
              ))
            ) : (
              <p className="text-sm text-gray-500">No local governments found for {state}.</p>
            )}
          </div>
        </div>
      )}
      
      {selectedLGA && (
        <div className="pt-2 border-t">
          <LGAHeader lgaName={selectedLGA} stateName={state} />
          <LGATabs state={state} lga={selectedLGA} isAdmin={isAdmin} />
        </div>
      )}
      
      <QRModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        url={referralUrl}
        title="Share Your Referral Link"
        description="When members join using your referral link, you'll earn rewards and recognition as a community builder"
      />
    </div>
  );
};

export default LGASection;
